import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { getDeliveryApiBaseUrl } from "src/utils/apiUtils"
import { getAuth0 } from "src/utils/auth0Utils"

// --- Order types ---

export type OrderSummary = {
  id: number
  reference: string
  state: string
  stateMachineWorkflow: "DELIVERY" | "PICKUP"
  deliveryDate: string
  desiredArrivalTime: string
  amount: number | null
  clientId: number
  addressId: number
  source: string
  dateCreated: string | null
  lastUpdated: string | null
}

export type OrderDetailAddress = {
  id: number
  city: string
  country: string
  postcode: string
  state: string
  streetName: string
  streetNumber: string
  appartNumber: string | null
  label: string | null
  notes: string | null
  positionId: number | null
}

export type OrderDetailClient = {
  id: number
  firstName: string
  lastName: string
  email: string | null
  organisation: string | null
  notes: string | null
  externalReference: string | null
  language: string | null
  archived: boolean
  phoneNumbers?: Array<{
    id: number
    number: string
    label: string | null
    clientId: number
  }>
  tags?: Array<{
    id: number
    clientTagsId: number
    tagId: number
    tag?: {
      id: number
      label: string
      type: string
      color: string
      icon: string
    }
  }>
}

export type OrderDetailHaulerEmployee = {
  id: number
  firstName: string
  lastName: string
  phoneNumber: string | null
  phoneNumberCheckinDate: string | null
  haulerId: number
}

export type OrderDetailRouteSummary = {
  id: number
  timestamp: string | null
  stopCount: number | null
  totalDistanceKm: number | null
  totalTravelTimeMin: number | null
}

export type OrderDetailBag = {
  id: number
  orderId: number
  bagTypeId: number
  number: number
  bagType?: { id: number; name: string; shortName: string | null }
}

export type OrderDetailTag = {
  orderTagsId: number
  tagId: number
  tag?: {
    id: number
    label: string
    color: string
    icon: string
    type: string
    shortLabel?: string
    displayOption?: string
  }
}

export type OrderDetail = OrderSummary & {
  notes: string | null
  callTime: string | null
  haulerEmployeeId: number | null
  routeSummaryId: number | null
  externalReference: string | null
  paymentType: string | null
  collectedAmount: number | null
  collectingAccount: string | null
  totalAmount: number | null
  calculatedArrivalTime: string | null
  desiredArrivalTimeHour: string | null
  desiredArrivalTimeHourEnd: string | null
  recipientIdentity: string | null
  recipientRelation: string | null
  recipientRelationMore: string | null
  manifestId: number | null
  sequence: number | null
  recurrenceSourceId: number | null
  address?: OrderDetailAddress
  client?: OrderDetailClient
  haulerEmployee?: OrderDetailHaulerEmployee
  routeSummary?: OrderDetailRouteSummary
  bags?: OrderDetailBag[]
  tags?: OrderDetailTag[]
}

export type OrderListResponse = {
  data: OrderSummary[]
  total: number
  pages: number
  page: number
  pageSize: number
}

export type OrderCreateInput = {
  amount?: number | null
  clientId: number
  deliveryDate: string
  desiredArrivalTime: string
  desiredArrivalTimeHour?: string | null
  desiredArrivalTimeHourEnd?: string | null
  state: string
  addressId?: number | null
  stateMachineWorkflow: string
  source: string
  notes?: string | null
  reference: string
  callTime?: string | null
  externalReference?: string | null
  paymentType?: string | null
  newAddress?: {
    streetNumber: string
    streetName: string
    appartNumber?: string | null
    city: string
    state?: string | null
    postcode: string
    country?: string | null
    label?: string | null
    notes?: string | null
  } | null
  recurrence?: {
    type:
      | "DAILY"
      | "XDAYS"
      | "WEEKLY"
      | "WORKDAYS"
      | "BIWEEKLY"
      | "FOURWEEKS"
      | "MONTHLY"
    dailyRecurringCriteria?: number
    endDate?: string
  } | null
  bags?: Array<{ bagTypeId: number; number: number }>
  prescriptionImages?: Array<{
    data: string
    filename: string
    mimeType: string
  }>
}

export type OrderTransitionResponse = {
  data: OrderDetail
  previousState: string
  newState: string
  possibleEvents: string[]
}

export type TraceabilityProofFile = {
  id: number
  data: string
  mimeType: string
  filename?: string
}

export type OrderTraceability = {
  id: number
  event: string
  sourceState: string
  targetState: string
  dateTime: string
  orderId: number
  actorId: number
  message: string | null
  operatorCode: string | null
  interlocutorIdentity: string | null
  interlocutorRelation: string | null
  interlocutorRelationMore: string | null
  clientSignatureId: number | null
  actor?: {
    id: number
    firstName: string
    lastName: string
    username: string
  } | null
  /** Signature image captured during this transition (base64 data URI) */
  signature?: {
    id: number
    data: string
    mimeType: string
  } | null
  /** Photos captured during this transition */
  photos?: TraceabilityProofFile[]
}

// --- Dashboard types ---

export type DashboardOrder = {
  id: number
  reference: string
  state: string
  stateMachineWorkflow: "DELIVERY" | "PICKUP"
  deliveryDate: string
  desiredArrivalTime: string
  amount: number | null
  clientId: number
  addressId: number
  source: string
  haulerEmployeeId: number | null
  notes: string | null
  dateCreated: string | null
  client?: { id: number; firstName: string; lastName: string }
  address?: {
    id: number
    streetName: string
    streetNumber: string
    city: string
    postcode: string
    appartNumber: string | null
    positionId: number | null
    position?: {
      id: number
      latitude: number
      longitude: number
      accuracy: number | null
    }
  }
  tags?: Array<{
    orderTagsId: number
    tagId: number
    tag?: { id: number; label: string; color: string; icon: string }
  }>
}

export type DashboardResponse = {
  data: DashboardOrder[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

export type DashboardCountsResponse = {
  total: number
  byState: Record<string, number>
}

// --- Manifest types ---

export type ManifestItem = {
  id: number
  acceptedOnDelivery: boolean | null
  item: { id: number; label: string; origin: string }
}

export type OrderManifest = {
  id: number
  confirmationText: { text: string } | null
  items: ManifestItem[]
}

export type OrderManifestResponse = {
  data: OrderManifest | null
  count: number
}

// --- Batch action types ---

export type BatchTransitionResult = {
  orderId: number
  success: boolean
  previousState?: string
  newState?: string
  error?: string
}

export type BatchTransitionResponse = {
  results: BatchTransitionResult[]
}

export type BatchAssignResult = {
  orderId: number
  success: boolean
  error?: string
}

export type BatchAssignResponse = {
  results: BatchAssignResult[]
}

export type AvailableCourier = {
  id: number
  firstName: string
  lastName: string
  haulerName: string
  haulerId: number
}

// --- Notification types ---

export type NotificationItem = {
  id: number
  title: string
  body: string
  key: string | null
  emissionDate: number
  displayed: boolean
  displayedDate: number | null
  read: boolean
  readDate: number | null
  recipientId: number
  orderId: number | null
  sound: boolean
  openOnClick: boolean | null
}

// --- Employee types ---

export type Employee = {
  id: number
  firstName: string
  lastName: string
  username: string
  enabled: boolean
  roles?: { id: number; role: string }[]
}

// --- Client types ---

export type ClientSummary = {
  id: number
  firstName: string
  lastName: string
  email: string | null
  organisation: string | null
  archived: boolean
  dateCreated: string
  lastUpdated: string
}

export type PhoneNumber = {
  id: number
  number: string
  label: string | null
  clientId: number
}

export type ClientTag = {
  id: number
  clientTagsId: number
  tagId: number
  tag?: { id: number; label: string; type: string; color: string; icon: string }
}

export type ClientAddress = {
  id: number
  clientAddressesId: number
  addressId: number
  address?: {
    id: number
    streetNumber: string
    streetName: string
    appartNumber: string | null
    city: string
    state: string
    country: string
    postcode: string
    label: string | null
    notes: string | null
    positionId: number | null
    position?: {
      id: number
      latitude: number
      longitude: number
      accuracy: number | null
    }
  }
}

export type ClientRecipientJunction = {
  id: number
  clientAuthorizedRecipientsId: number
  recipientId: number
  recipient?: {
    id: number
    name: string
    relation: string
  }
}

export type ClientDetail = ClientSummary & {
  notes: string | null
  language: string | null
  externalReference: string | null
  includesInSurvey: boolean
  includesInTakeInChargeNotifications: boolean
  lastCompletedOrderDate: string | null
  userId: number | null
  phoneNumbers: PhoneNumber[]
  tags: ClientTag[]
  addresses: ClientAddress[]
  recipients: ClientRecipientJunction[]
}

export type ClientListResponse = {
  data: ClientSummary[]
  total: number
  pages: number
  page: number
  pageSize: number
}

export type ClientCreateInput = {
  firstName: string
  lastName: string
  email?: string | null
  organisation?: string | null
  notes?: string | null
  language?: string | null
  archived?: boolean
  externalReference?: string | null
  includesInSurvey?: boolean
  includesInTakeInChargeNotifications?: boolean
}

export type ClientImportRow = {
  firstName: string
  lastName: string
  email?: string | null
  phone?: string | null
  streetNumber?: string | null
  streetName?: string | null
  apartment?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  country?: string | null
  notes?: string | null
  tags?: string[] | null
}

export type ClientImportResult = {
  created: number
  updated: number
  failed: number
  errors: { row: number; message: string }[]
}

// --- Phone bulk update types (F093) ---

export type PhoneBulkUpdateRow = {
  clientId: number
  phone: string
  label?: "HOME" | "WORK" | "MOBILE" | "OTHER"
}

export type PhoneBulkUpdateResult = {
  created: number
  updated: number
  failed: number
  errors: { row: number; message: string }[]
}

// --- Client duplicate detection types ---

export type ClientDuplicateInfo = {
  id: number
  firstName: string
  lastName: string
  email: string | null
  organisation: string | null
  dateCreated: string | null
  phoneCount: number
  orderCount: number
}

export type DuplicateGroup = {
  matchType: "name" | "phone" | "email"
  matchValue: string
  clients: ClientDuplicateInfo[]
}

export type ClientDuplicatesResponse = {
  groups: DuplicateGroup[]
}

export type MergeClientsInput = {
  primaryClientId: number
  duplicateClientIds: number[]
}

export type MergeClientsResult = {
  merged: number
  primaryClient: {
    id: number
    firstName: string
    lastName: string
  }
}

export type AddressCreateInput = {
  streetNumber: string
  streetName: string
  appartNumber?: string | null
  city: string
  state: string
  country: string
  postcode: string
  label?: string | null
  notes?: string | null
}

export type AddressItem = {
  id: number
  streetNumber: string
  streetName: string
  appartNumber: string | null
  city: string
  state: string
  country: string
  postcode: string
  label: string | null
  notes: string | null
}

// --- Report types ---

export type ReportParameters = {
  appConfig: {
    clientExternalReferenceEnabled: boolean
    orderExternalReferenceEnabled: boolean
    multipleCollectingAccountsEnabled: boolean
  }
  maxDaysAllowedForReports: number
  distanceUnit: string
  dateTimeFormat: string
}

export type ReportTraceability = {
  id: number
  dateTime: string
  event: string
  message: string | null
  operatorCode: string | null
  sourceState: string
  targetState: string
  actor: {
    id: number
    firstName: string
    lastName: string
    username: string
  } | null
  position: {
    latitude: number
    longitude: number
    accuracy: number | null
  } | null
  interlocutor: {
    identity: string
    relation: string
    relationMore: string | null
  } | null
  clientSignatureId: number | null
  withSignature: boolean
}

export type ReportOrder = {
  id: number
  reference: string
  status: string
  amount: number
  collectedAmount: number
  collectingAccount: string
  paymentType: string
  externalReference: string
  source: string
  deliveryDate: string
  dateCallTime: string | null
  workflow: string
  notes: string
  withNotes: boolean
  providerId: string
  providerName: string
  providerCode: string
  address: {
    id: number
    streetNumber: string
    streetName: string
    appartNumber: string
    city: string
    postcode: string
    country: string
    state: string
    label: string
    notes: string
    lockerName: string
    position: {
      latitude: number
      longitude: number
      accuracy: number | null
    } | null
  } | null
  client: {
    id: number
    firstName: string
    lastName: string
    externalReference: string
    organisation: string
  } | null
  timeConstraint: { start: string; end: string } | null
  tags: Array<{
    id: number
    label: string
    color: string
    icon: string
    shortLabel?: string
    type?: string
  }>
  bags: Array<{
    id: number
    number: number
    bagType: { id: number; shortName: string; name: string } | null
  }>
}

export type ReportEntry = {
  order: ReportOrder
  creationTraceability: ReportTraceability | null
  departureTraceability: ReportTraceability | null
  traceability: ReportTraceability | null
  courier: {
    id: number
    firstName: string
    lastName: string
    username: string
    hauler: { id: number; name: string } | null
  } | null
  provider: string
  distance: { value: number; unit: string } | null
  joined: boolean
  isUrgent: boolean
  tags: Array<{ id: number; label: string; color: string; icon: string }>
  totalServiceTimeMs: number
  imageCount: number
}

export type ReportCourier = {
  id: number
  firstName: string
  lastName: string
  username: string
  hauler: { id: number; name: string } | null
}

export type ReportProvider = {
  id: string
  name: string
  code?: string
}

export type ReportTagCount = {
  count: number
  tag: { id: number; label: string; color: string; icon: string }
}

export type UniversalReport = {
  entries: ReportEntry[]
  couriers: ReportCourier[]
  providers: ReportProvider[]
  tags: ReportTagCount[]
  providerSize: number
  nbEmergencyOrders: number
  numberOfJoined: number
  withSomeCallTime: boolean
  withServiceTime: boolean
}

// --- Immobility Report types ---

export type ImmobilityReportItem = {
  orderId: number
  reference: string
  clientName: string
  state: string
  stuckSince: string | null
  hoursStuck: number | null
  lastEvent: string | null
  lastEventSourceState: string | null
  lastEventTargetState: string | null
  haulerEmployeeId: number | null
  haulerEmployeeName: string | null
}

export type ImmobilityReportResponse = {
  deliveryDate: string
  thresholdHours: number
  totalStuckOrders: number
  items: ImmobilityReportItem[]
}

export type ImmobilityReportRequest = {
  deliveryDate: string
  thresholdHours: number
  haulerEmployeeIds?: number[]
}

// --- CourierLink types ---

export type LinkHauler = {
  id: number
  name: string
  code: string
  address: string | null
}

export type LinkConfig = {
  positionVisibility: "ALWAYS" | "ONLY_WHEN_CARRYING" | "NEVER"
  etaVisibility: "ALWAYS" | "NEVER"
  numberOrderInCar: "ALL" | "NONE"
}

export type CourierLinkItem = {
  id: number
  hauler: LinkHauler
  state:
    | "ESTABLISHED"
    | "INITIATED_WAITING_HAULER"
    | "INITIATED_WAITING_PROVIDER"
    | "REFUSED"
  acceptedDate: number | null
  acceptingUser: { firstName: string; lastName: string } | null
  initiationDate: string
  config: LinkConfig | null
}

export type CourierLinkListResponse = {
  data: CourierLinkItem[]
  total: number
  page: number
  pageSize: number
}

// --- Courier types ---

export type CourierRole = {
  id: number
  authority: string
}

export type CourierItem = {
  id: number
  firstName: string
  lastName: string
  phoneNumber: string | null
  email: string | null
  username: string
  userId: number
  enabled: boolean
  roles: CourierRole[]
  lastLogin: string | null
  dateCreated: string | null
  appSettings: string | null
  mobileData: string | null
}

export type CourierDevice = {
  id: number
  uuid: string
  platform: string | null
  manufacturer: string | null
  model: string | null
  osVersion: string | null
  appName: string | null
  appNativeVersion: string | null
  appUnifiedVersion: string | null
  batteryCharge: string | null
  gpsStatus: string | null
  networkType: string | null
  carrierName: string | null
  dateCreated: string
  lastUpdated: string
}

export type CourierVehicleInfo = {
  licensePlate?: string | null
  vehicleType?: string | null
}

export type CourierListResponse = {
  data: CourierItem[]
  total: number
  page: number
  pageSize: number
}

export type CourierCreateInput = {
  firstName: string
  lastName: string
  username: string
  phoneNumber?: string | null
  email?: string | null
  isManager?: boolean
}

export type CourierUpdateInput = {
  firstName?: string
  lastName?: string
  phoneNumber?: string | null
  email?: string | null
  isManager?: boolean
  vehicleInfo?: CourierVehicleInfo | null
}

// --- Address types (F094-F096) ---

export type AddressListItem = {
  id: number
  streetNumber: string | null
  streetName: string | null
  appartNumber: string | null
  city: string | null
  state: string | null
  country: string | null
  postcode: string | null
  label: string | null
  notes: string | null
  dateCreated: string | null
  positionId: number | null
  latitude: number | null
  longitude: number | null
  clientId: number | null
  clientFirstName: string | null
  clientLastName: string | null
  clientOrganisation: string | null
}

export type AddressUpdateInput = {
  streetNumber?: string
  streetName?: string
  appartNumber?: string | null
  city?: string
  state?: string | null
  country?: string
  postcode?: string
  label?: string | null
  notes?: string | null
}

// --- Provider types ---

export type ProviderDetail = {
  id: number
  name: string
  code: string
  email: string | null
  phone: string | null
  timezone: string | null
  address?: {
    id: number
    streetName: string
    streetNumber: string
    city: string
    state: string | null
    country: string | null
    postcode: string
    appartNumber: string | null
    positionId: number | null
    position?: {
      id: number
      latitude: number
      longitude: number
      accuracy: number | null
    }
  }
  [key: string]: any
}

// --- Recurring Order types ---

export type RecurringOrderSummary = {
  id: number
  clientId: number
  addressId: number
  recurrenceType: string
  stateMachineWorkflow: string
  desiredArrivalTime: string
  startDate: string
  endDate: string | null
  dailyRecurringCriteria: number | null
  weeklyRecurringCriteria: number | null
  monthlyRecurringCriteria: number | null
  dateCreated: string | null
  lastUpdated: string | null
  notes: string | null
  externalReference: string | null
  haulerEmployeeId: number | null
  desiredArrivalTimeHour: string | null
  desiredArrivalTimeHourEnd: string | null
  manifestId: number | null
  client?: { id: number; firstName: string; lastName: string }
  address?: {
    id: number
    streetName: string
    streetNumber: string
    city: string
    postcode: string
    appartNumber: string | null
    label: string | null
  }
}

export type RecurringOrderDetail = RecurringOrderSummary & {
  childOrders: Array<{
    id: number
    reference: string
    state: string
    deliveryDate: string
    recurringOrderPlanifiedDate: string | null
    dateCreated: string | null
  }>
}

export type RecurringOrderUpdateInput = {
  recurrenceType?: string
  dailyRecurringCriteria?: number | null
  weeklyRecurringCriteria?: number | null
  monthlyRecurringCriteria?: number | null
  desiredArrivalTimeHour?: string | null
  desiredArrivalTimeHourEnd?: string | null
  notes?: string | null
  externalReference?: string | null
  version: number
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getDeliveryApiBaseUrl(),
  prepareHeaders: async (headers) => {
    headers.set("Content-Type", "application/json; charset=utf-8")
    headers.set("ts", Date.now().toString())

    try {
      const auth0 = getAuth0()
      const token = await auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: "mbls-global-api",
        },
      })
      if (token) {
        headers.set("Authorization", `Bearer ${token}`)
      }
    } catch (error) {
      console.error("Error fetching Auth0 token:", error)
    }

    return headers
  },
})

const appBaseQuery: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions)

  // Log dev trace IDs so Playwright can read them from console
  if (import.meta.env.DEV) {
    const meta = result.meta as any
    const resp = meta?.response as Response | undefined
    const traceId = resp?.headers?.get("x-mbls-dev-trace-id")
    if (traceId) {
      const url =
        meta?.request?.url ?? (typeof args === "string" ? args : args.url)
      console.log(`[devtrace] ${url} ${traceId}`)
    }
  }

  return result
}

export const appApi = createApi({
  reducerPath: "appApi",
  baseQuery: appBaseQuery,
  tagTypes: [
    "Orders",
    "Employees",
    "Clients",
    "Tags",
    "Configurations",
    "Provider",
    "Notifications",
    "Reports",
    "Links",
    "Couriers",
    "RecurringOrders",
    "BagTypes",
    "Addresses",
  ],
  endpoints: (builder) => ({
    // Orders
    getOrderById: builder.query<
      {
        data: OrderDetail
        possibleEvents: string[]
        canUndo: boolean
        undoTargetState: string | null
      },
      number
    >({
      query: (id) => `/api/orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Orders", id }],
    }),
    getOrderTraceabilities: builder.query<
      { data: OrderTraceability[]; count: number },
      number
    >({
      query: (id) => `/api/orders/${id}/traceabilities`,
      providesTags: (_result, _error, id) => [{ type: "Orders", id }],
    }),
    createOrder: builder.mutation<{ data: OrderDetail }, OrderCreateInput>({
      query: (body) => ({
        url: "/api/orders",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Orders", id: "LIST" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
      ],
    }),
    transitionOrder: builder.mutation<
      OrderTransitionResponse,
      { id: number; event: string; operatorCode: string; message?: string }
    >({
      query: ({ id, event, operatorCode, message }) => ({
        url: `/api/orders/${id}/transition`,
        method: "POST",
        body: { event, operatorCode, ...(message ? { message } : {}) },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Orders", id },
        { type: "Orders", id: "LIST" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
      ],
    }),
    // Undo transition (F056)
    undoTransitionOrder: builder.mutation<
      OrderTransitionResponse & {
        canUndo: boolean
        undoTargetState: string | null
      },
      { id: number; operatorCode: string; message?: string }
    >({
      query: ({ id, operatorCode, message }) => ({
        url: `/api/orders/${id}/undo-transition`,
        method: "POST",
        body: { operatorCode, ...(message ? { message } : {}) },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Orders", id },
        { type: "Orders", id: "LIST" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
      ],
    }),

    // Dashboard
    getDashboardOrders: builder.query<
      DashboardResponse,
      {
        deliveryDate: string
        page?: number
        pageSize?: number
        states?: string[]
      }
    >({
      query: ({ deliveryDate, page = 1, pageSize = 200, states }) => {
        const params = new URLSearchParams({
          deliveryDate,
          page: String(page),
          pageSize: String(pageSize),
        })
        if (states?.length) {
          params.set("states", states.join(","))
        }
        return `/api/orders/dashboard?${params.toString()}`
      },
      providesTags: [{ type: "Orders", id: "DASHBOARD" }],
    }),
    getDashboardCounts: builder.query<
      DashboardCountsResponse,
      { deliveryDate: string }
    >({
      query: ({ deliveryDate }) =>
        `/api/orders/dashboard/counts?deliveryDate=${deliveryDate}`,
      providesTags: [{ type: "Orders", id: "DASHBOARD_COUNTS" }],
    }),
    getExportOrders: builder.query<
      { data: DashboardOrder[] },
      { deliveryDate: string; states?: string[]; orderIds?: number[] }
    >({
      query: ({ deliveryDate, states, orderIds }) => {
        const params = new URLSearchParams({ deliveryDate })
        if (states?.length) {
          params.set("states", states.join(","))
        }
        if (orderIds?.length) {
          params.set("orderIds", orderIds.join(","))
        }
        return `/api/orders/export?${params.toString()}`
      },
    }),
    batchTransitionOrders: builder.mutation<
      BatchTransitionResponse,
      {
        orderIds: number[]
        event: string
        operatorCode: string
        message?: string
      }
    >({
      query: ({ orderIds, event, operatorCode, message }) => ({
        url: "/api/orders/batch-transition",
        method: "POST",
        body: {
          orderIds,
          event,
          operatorCode,
          ...(message ? { message } : {}),
        },
      }),
      invalidatesTags: [
        { type: "Orders", id: "DASHBOARD" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
        { type: "Orders", id: "LIST" },
      ],
    }),
    // Batch assign orders to a courier (F064)
    batchAssignOrders: builder.mutation<
      BatchAssignResponse,
      { orderIds: number[]; haulerEmployeeId: number }
    >({
      query: (body) => ({
        url: "/api/orders/batch-assign",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Orders", id: "DASHBOARD" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
        { type: "Orders", id: "LIST" },
      ],
    }),
    // Bulk update orders: add notes and/or tags (F065)
    bulkUpdateOrders: builder.mutation<
      { results: { orderId: number; success: boolean; error?: string }[] },
      { orderIds: number[]; notes?: string; tagIds?: number[] }
    >({
      query: (body) => ({
        url: "/api/orders/bulk-update",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Orders", id: "DASHBOARD" },
        { type: "Orders", id: "DASHBOARD_COUNTS" },
        { type: "Orders", id: "LIST" },
      ],
    }),
    // Available couriers for order assignment (F064)
    getAvailableCouriers: builder.query<{ data: AvailableCourier[] }, void>({
      query: () => "/api/orders/available-couriers",
    }),
    // Order manifest items (F060)
    getOrderManifestItems: builder.query<OrderManifestResponse, number>({
      query: (orderId) => `/api/orders/${orderId}/manifestItems`,
      providesTags: (_r, _e, id) => [{ type: "Orders", id }],
    }),

    // Employees
    getEmployees: builder.query<
      Employee[],
      { withDisabled?: boolean; search?: string } | void
    >({
      query: (params) => {
        const withDisabled =
          params && "withDisabled" in params ? params.withDisabled : false
        const search = params && "search" in params ? params.search : ""
        const qs = new URLSearchParams()
        qs.set("withDisabled", String(withDisabled))
        if (search) qs.set("search", search)
        return `/api/employees?${qs.toString()}`
      },
      transformResponse: (response: { data: Employee[] }) => response.data,
      providesTags: ["Employees"],
    }),
    createEmployee: builder.mutation<
      any,
      {
        firstName: string
        lastName: string
        username: string
        sharedAccess?: boolean
        administrator?: boolean
      }
    >({
      query: (body) => ({
        url: "/api/employees",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Employees"],
    }),
    updateEmployee: builder.mutation<
      any,
      { id: number; firstName?: string; lastName?: string; enabled?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/employees/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Employees"],
    }),
    deleteEmployee: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/employees/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Employees"],
    }),
    resetEmployeePassword: builder.mutation<
      { message: string; employeeId: number; username: string },
      number
    >({
      query: (id) => ({
        url: `/api/employees/${id}/reset-password`,
        method: "POST",
      }),
    }),

    // Clients
    getClients: builder.query<
      ClientListResponse,
      {
        page?: number
        pageSize?: number
        search?: string
        sortField?: string
        sortOrder?: "asc" | "desc"
      }
    >({
      query: (params) => {
        const qs = new URLSearchParams()
        if (params.page) qs.set("page", String(params.page))
        if (params.pageSize) qs.set("pageSize", String(params.pageSize))
        if (params.search) qs.set("search", params.search)
        if (params.sortField) qs.set("sortField", params.sortField)
        if (params.sortOrder) qs.set("sortOrder", params.sortOrder)
        const query = qs.toString()
        return `/api/clients${query ? `?${query}` : ""}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Clients" as const,
                id,
              })),
              { type: "Clients", id: "LIST" },
            ]
          : [{ type: "Clients", id: "LIST" }],
    }),
    getClientById: builder.query<{ data: ClientDetail }, number>({
      query: (id) => `/api/clients/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Clients", id }],
    }),
    createClient: builder.mutation<{ data: ClientDetail }, ClientCreateInput>({
      query: (body) => ({
        url: "/api/clients",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),
    updateClient: builder.mutation<
      { data: ClientDetail },
      { id: number; body: Partial<ClientCreateInput> }
    >({
      query: ({ id, body }) => ({
        url: `/api/clients/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Clients", id },
        { type: "Clients", id: "LIST" },
      ],
    }),
    deleteClient: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/api/clients/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Clients", id },
        { type: "Clients", id: "LIST" },
      ],
    }),

    // Client phone numbers
    createPhoneNumber: builder.mutation<
      { data: PhoneNumber },
      { clientId: number; number: string; label?: string | null }
    >({
      query: ({ clientId, ...body }) => ({
        url: `/api/clients/${clientId}/phone-numbers`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    updatePhoneNumber: builder.mutation<
      { data: PhoneNumber },
      { clientId: number; id: number; number?: string; label?: string | null }
    >({
      query: ({ clientId, id, ...body }) => ({
        url: `/api/clients/${clientId}/phone-numbers/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    deletePhoneNumber: builder.mutation<
      { success: boolean },
      { clientId: number; id: number }
    >({
      query: ({ clientId, id }) => ({
        url: `/api/clients/${clientId}/phone-numbers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),

    // Client addresses
    createClientAddress: builder.mutation<
      { data: AddressItem },
      { clientId: number } & AddressCreateInput
    >({
      query: ({ clientId, ...body }) => ({
        url: `/api/clients/${clientId}/addresses`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    updateClientAddress: builder.mutation<
      { data: AddressItem },
      { clientId: number; addressId: number } & Partial<AddressCreateInput>
    >({
      query: ({ clientId, addressId, ...body }) => ({
        url: `/api/clients/${clientId}/addresses/${addressId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    deleteClientAddress: builder.mutation<
      { success: boolean },
      { clientId: number; addressId: number }
    >({
      query: ({ clientId, addressId }) => ({
        url: `/api/clients/${clientId}/addresses/${addressId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),

    // Client orders (order history)
    getClientOrders: builder.query<
      OrderListResponse,
      { clientId: number; page?: number; pageSize?: number; state?: string }
    >({
      query: ({ clientId, ...params }) => {
        const qs = new URLSearchParams()
        if (params.page) qs.set("page", String(params.page))
        if (params.pageSize) qs.set("pageSize", String(params.pageSize))
        if (params.state) qs.set("state", params.state)
        const query = qs.toString()
        return `/api/clients/${clientId}/orders${query ? `?${query}` : ""}`
      },
      providesTags: [{ type: "Orders", id: "LIST" }],
    }),
    // Client order suggestions (recent orders with populated data for auto-fill)
    getClientOrderSuggestions: builder.query<
      {
        data: DashboardOrder[]
        total: number
        pages: number
        page: number
        pageSize: number
      },
      { clientId: number }
    >({
      query: ({ clientId }) =>
        `/api/clients/${clientId}/orders?pageSize=5&populate=true`,
      providesTags: [{ type: "Orders", id: "LIST" }],
    }),

    // Client tags (link/unlink)
    linkClientTag: builder.mutation<any, { clientId: number; tagId: number }>({
      query: ({ clientId, tagId }) => ({
        url: `/api/clients/${clientId}/tags/${tagId}`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    unlinkClientTag: builder.mutation<any, { clientId: number; tagId: number }>(
      {
        query: ({ clientId, tagId }) => ({
          url: `/api/clients/${clientId}/tags/${tagId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_r, _e, { clientId }) => [
          { type: "Clients", id: clientId },
        ],
      },
    ),

    // Authorized recipients
    createAuthorizedRecipient: builder.mutation<
      { data: { id: number; name: string; relation: string } },
      { clientId: number; name: string; relation: string }
    >({
      query: ({ clientId, ...body }) => ({
        url: `/api/clients/${clientId}/authorized-recipients`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    updateAuthorizedRecipient: builder.mutation<
      { data: { id: number; name: string; relation: string } },
      { clientId: number; id: number; name?: string; relation?: string }
    >({
      query: ({ clientId, id, ...body }) => ({
        url: `/api/clients/${clientId}/authorized-recipients/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),
    deleteAuthorizedRecipient: builder.mutation<
      { success: boolean },
      { clientId: number; id: number }
    >({
      query: ({ clientId, id }) => ({
        url: `/api/clients/${clientId}/authorized-recipients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { clientId }) => [
        { type: "Clients", id: clientId },
      ],
    }),

    // Client bulk import
    importClients: builder.mutation<
      ClientImportResult,
      { rows: ClientImportRow[] }
    >({
      query: (body) => ({
        url: "/api/clients/import",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),

    // Phone bulk update (F093)
    phoneBulkUpdate: builder.mutation<
      PhoneBulkUpdateResult,
      { rows: PhoneBulkUpdateRow[] }
    >({
      query: (body) => ({
        url: "/api/clients/phone-bulk-update",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),

    // Client duplicate detection
    getClientDuplicates: builder.query<ClientDuplicatesResponse, void>({
      query: () => "/api/clients/duplicates",
      providesTags: [{ type: "Clients", id: "DUPLICATES" }],
    }),
    mergeClients: builder.mutation<MergeClientsResult, MergeClientsInput>({
      query: (body) => ({
        url: "/api/clients/merge",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Clients", id: "LIST" },
        { type: "Clients", id: "DUPLICATES" },
      ],
    }),

    // Tags (filtered by type — used by order/client forms)
    getTags: builder.query<any[], { type: string }>({
      query: ({ type }) => `/api/tags?type=${type}`,
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ["Tags"],
    }),

    // Tags CRUD (all tags — used by configuration page)
    getAllTags: builder.query<any[], void>({
      query: () => "/api/tags",
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ["Tags"],
    }),
    createTag: builder.mutation<
      any,
      {
        label: string
        type: string
        color: string
        icon: string
        shortLabel?: string
        displayOption?: string
        onMobile?: boolean
      }
    >({
      query: (body) => ({
        url: "/api/tags",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tags"],
    }),
    updateTag: builder.mutation<any, { id: number; body: Record<string, any> }>(
      {
        query: ({ id, body }) => ({
          url: `/api/tags/${id}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["Tags"],
      },
    ),
    deleteTag: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/tags/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tags"],
    }),

    // Configurations CRUD
    getConfigurations: builder.query<any[], void>({
      query: () => "/api/configurations",
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ["Configurations"],
    }),
    createConfiguration: builder.mutation<
      any,
      { name: string; value: string; enabled: boolean }
    >({
      query: (body) => ({
        url: "/api/configurations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Configurations"],
    }),
    updateConfiguration: builder.mutation<
      any,
      { id: number; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/api/configurations/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Configurations"],
    }),
    deleteConfiguration: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/configurations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Configurations"],
    }),

    // Bag Types CRUD
    getBagTypes: builder.query<any[], void>({
      query: () => "/api/bag-types",
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ["BagTypes"],
    }),
    createBagType: builder.mutation<any, { name: string; shortName: string }>({
      query: (body) => ({
        url: "/api/bag-types",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BagTypes"],
    }),
    updateBagType: builder.mutation<
      any,
      { id: number; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/api/bag-types/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["BagTypes"],
    }),
    deleteBagType: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/bag-types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BagTypes"],
    }),

    // Provider detail
    getProviderDetail: builder.query<ProviderDetail, void>({
      query: () => "/api/provider",
      transformResponse: (response: { data: ProviderDetail }) => response.data,
      providesTags: ["Provider"],
    }),
    // Provider timezone update (F163)
    updateProviderTimezone: builder.mutation<
      { data: ProviderDetail },
      { timeZone: string }
    >({
      query: (body) => ({
        url: "/api/provider/timezone",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Provider"],
    }),
    // Provider logo (F134)
    getProviderLogo: builder.query<
      { id: number; filename: string; mimeType: string; data: string } | null,
      void
    >({
      query: () => "/api/provider/logo",
      transformResponse: (response: {
        data: {
          id: number
          filename: string
          mimeType: string
          data: string
        } | null
      }) => response.data,
      providesTags: ["Provider"],
    }),
    uploadProviderLogo: builder.mutation<
      { data: { logoId: number } },
      { data: string; filename: string; mimeType: string }
    >({
      query: (body) => ({
        url: "/api/provider/logo",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Provider"],
    }),

    // Addresses (F094-F096)
    getAddresses: builder.query<
      {
        data: AddressListItem[]
        total: number
        page: number
        pageSize: number
      },
      {
        page?: number
        pageSize?: number
        search?: string
        sortField?: string
        sortOrder?: string
      }
    >({
      query: (params) => ({
        url: "/api/addresses",
        params,
      }),
      providesTags: ["Addresses"],
    }),
    updateAddress: builder.mutation<
      { data: any },
      { id: number; body: Partial<AddressUpdateInput> }
    >({
      query: ({ id, body }) => ({
        url: `/api/addresses/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Addresses"],
    }),
    deleteAddress: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/api/addresses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Addresses"],
    }),

    // Notifications
    getNotifications: builder.query<{ data: NotificationItem[] }, void>({
      query: () => "/api/notifications",
      providesTags: ["Notifications"],
    }),
    markNotificationRead: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/notifications/${id}/read`,
        method: "POST",
      }),
      invalidatesTags: ["Notifications"],
    }),
    markAllNotificationsRead: builder.mutation<{ count: number }, void>({
      query: () => ({
        url: "/api/notifications/read-all",
        method: "POST",
      }),
      invalidatesTags: ["Notifications"],
    }),

    // IoT credentials for MQTT subscription
    getIotCreds: builder.query<any, void>({
      query: () => "/api/iot/creds",
    }),

    // Reports
    getReportParameters: builder.query<ReportParameters, void>({
      query: () => "/api/reports/parameters",
      providesTags: ["Reports"],
    }),
    fetchReport: builder.mutation<
      UniversalReport,
      { from: string; to: string }
    >({
      query: (body) => ({
        url: "/api/reports",
        method: "POST",
        body,
      }),
    }),
    fetchImmobilityReport: builder.mutation<
      ImmobilityReportResponse,
      ImmobilityReportRequest
    >({
      query: (body) => ({
        url: "/api/reports/immobility",
        method: "POST",
        body,
      }),
    }),
    // Courier Links
    getLinks: builder.query<
      CourierLinkListResponse,
      {
        page?: number
        pageSize?: number
        search?: string
        sortField?: string
        sortOrder?: "asc" | "desc"
        status?: string
      }
    >({
      query: (params) => {
        const qs = new URLSearchParams()
        if (params.page) qs.set("page", String(params.page))
        if (params.pageSize) qs.set("pageSize", String(params.pageSize))
        if (params.search) qs.set("search", params.search)
        if (params.sortField) qs.set("sortField", params.sortField)
        if (params.sortOrder) qs.set("sortOrder", params.sortOrder)
        if (params.status) qs.set("status", params.status)
        const query = qs.toString()
        return `/api/links${query ? `?${query}` : ""}`
      },
      transformResponse: (response: {
        data: any[]
        total: number
        page: number
        pageSize: number
      }): CourierLinkListResponse => ({
        ...response,
        data: response.data.map((row: any) => ({
          id: row.id,
          hauler: {
            id: row.hauler_id ?? row.haulerId,
            name: row.haulerName ?? "",
            code: row.haulerCode ?? "",
            address: row.haulerAddress ?? null,
          },
          state: row.state,
          acceptedDate: row.accepted_date ?? row.acceptedDate ?? null,
          acceptingUser:
            row.acceptingFirstName || row.acceptingLastName
              ? {
                  firstName: row.acceptingFirstName ?? "",
                  lastName: row.acceptingLastName ?? "",
                }
              : null,
          initiationDate: row.initiation_date ?? row.initiationDate ?? "",
          config:
            row.config_id ?? row.configId
              ? {
                  positionVisibility: row.positionVisibility ?? "NEVER",
                  etaVisibility: row.etaVisibility ?? "NEVER",
                  numberOrderInCar: row.numberOrderInCar ?? "NONE",
                }
              : null,
        })),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Links" as const, id })),
              { type: "Links", id: "LIST" },
            ]
          : [{ type: "Links", id: "LIST" }],
    }),
    createLink: builder.mutation<
      { data: CourierLinkItem },
      { haulerCode: string }
    >({
      query: (body) => ({ url: "/api/links", method: "POST", body }),
      invalidatesTags: [{ type: "Links", id: "LIST" }],
    }),
    acceptLink: builder.mutation<{ data: CourierLinkItem }, number>({
      query: (id) => ({ url: `/api/links/${id}/accept`, method: "POST" }),
      invalidatesTags: [{ type: "Links", id: "LIST" }],
    }),
    deleteLink: builder.mutation<{ data: CourierLinkItem }, number>({
      query: (id) => ({ url: `/api/links/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "Links", id: "LIST" }],
    }),
    updateLinkConfig: builder.mutation<
      { data: { ok: true } },
      {
        id: number
        positionVisibility: string
        etaVisibility: string
        numberOrderInCar: string
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/links/${id}/config`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Links", id: "LIST" }],
    }),
    reactivateLink: builder.mutation<{ data: CourierLinkItem }, number>({
      query: (id) => ({ url: `/api/links/${id}/reactivate`, method: "POST" }),
      invalidatesTags: [{ type: "Links", id: "LIST" }],
    }),

    // Couriers
    getCouriers: builder.query<
      CourierListResponse,
      {
        page?: number
        pageSize?: number
        search?: string
        sortField?: string
        sortOrder?: "asc" | "desc"
        showDisabled?: boolean
      }
    >({
      query: (params) => {
        const qs = new URLSearchParams()
        if (params.page) qs.set("page", String(params.page))
        if (params.pageSize) qs.set("pageSize", String(params.pageSize))
        if (params.search) qs.set("search", params.search)
        if (params.sortField) qs.set("sortField", params.sortField)
        if (params.sortOrder) qs.set("sortOrder", params.sortOrder)
        qs.set("showDisabled", String(!!params.showDisabled))
        qs.set("_t", String(Date.now()))
        const query = qs.toString()
        return `/api/couriers${query ? `?${query}` : ""}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Couriers" as const,
                id,
              })),
              { type: "Couriers", id: "LIST" },
            ]
          : [{ type: "Couriers", id: "LIST" }],
    }),
    getCourierById: builder.query<{ data: CourierItem }, number>({
      query: (id) => `/api/couriers/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Couriers", id }],
    }),
    createCourier: builder.mutation<{ data: any }, CourierCreateInput>({
      query: (body) => ({ url: "/api/couriers", method: "POST", body }),
      invalidatesTags: [{ type: "Couriers", id: "LIST" }],
    }),
    updateCourier: builder.mutation<
      { success: boolean },
      { id: number; body: CourierUpdateInput }
    >({
      query: ({ id, body }) => ({
        url: `/api/couriers/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Couriers", id },
        { type: "Couriers", id: "LIST" },
      ],
    }),
    toggleCourier: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({ url: `/api/couriers/${id}/toggle`, method: "POST" }),
      invalidatesTags: [{ type: "Couriers", id: "LIST" }],
    }),
    // Courier devices (F110)
    getCourierDevices: builder.query<{ data: CourierDevice[] }, number>({
      query: (id) => `/api/couriers/${id}/devices`,
      providesTags: (_r, _e, id) => [{ type: "Couriers", id: `${id}-devices` }],
    }),
    // Send push message to courier (F111)
    sendCourierPushMessage: builder.mutation<
      { success: boolean; deviceCount: number },
      { id: number; message: string }
    >({
      query: ({ id, message }) => ({
        url: `/api/couriers/${id}/push-message`,
        method: "POST",
        body: { message },
      }),
    }),
    // Send remote command to courier (F112)
    sendCourierRemoteCommand: builder.mutation<
      { success: boolean; deviceCount: number },
      { id: number; command: string }
    >({
      query: ({ id, command }) => ({
        url: `/api/couriers/${id}/remote-command`,
        method: "POST",
        body: { command },
      }),
    }),

    // Recurring Orders
    getRecurringOrders: builder.query<
      {
        data: RecurringOrderSummary[]
        total: number
        pages: number
        page: number
        pageSize: number
      },
      {
        page?: number
        pageSize?: number
        sortField?: string
        sortOrder?: string
        status?: string
      }
    >({
      query: (params) => {
        const qs = new URLSearchParams()
        if (params.page) qs.set("page", String(params.page))
        if (params.pageSize) qs.set("pageSize", String(params.pageSize))
        if (params.sortField) qs.set("sortField", params.sortField)
        if (params.sortOrder) qs.set("sortOrder", params.sortOrder)
        if (params.status) qs.set("status", params.status)
        const query = qs.toString()
        return `/api/recurring-orders${query ? `?${query}` : ""}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "RecurringOrders" as const,
                id,
              })),
              { type: "RecurringOrders", id: "LIST" },
            ]
          : [{ type: "RecurringOrders", id: "LIST" }],
    }),
    getRecurringOrderById: builder.query<
      { data: RecurringOrderDetail },
      number
    >({
      query: (id) => `/api/recurring-orders/${id}`,
      providesTags: (_r, _e, id) => [{ type: "RecurringOrders", id }],
    }),
    updateRecurringOrder: builder.mutation<
      any,
      { id: number; body: RecurringOrderUpdateInput }
    >({
      query: ({ id, body }) => ({
        url: `/api/recurring-orders/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "RecurringOrders", id },
        { type: "RecurringOrders", id: "LIST" },
      ],
    }),
    skipRecurringOrderDate: builder.mutation<
      any,
      { id: number; date: string; operatorCode?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/recurring-orders/${id}/skip`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "RecurringOrders", id },
        { type: "Orders", id: "LIST" },
      ],
    }),
    stopRecurringOrder: builder.mutation<any, { id: number; endDate: string }>({
      query: ({ id, ...body }) => ({
        url: `/api/recurring-orders/${id}/stop`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "RecurringOrders", id },
        { type: "RecurringOrders", id: "LIST" },
      ],
    }),
    resumeRecurringOrder: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/recurring-orders/${id}/resume`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "RecurringOrders", id },
        { type: "RecurringOrders", id: "LIST" },
      ],
    }),
  }),
})

export const {
  useGetOrderByIdQuery,
  useCreateOrderMutation,
  useTransitionOrderMutation,
  useUndoTransitionOrderMutation,
  useGetDashboardOrdersQuery,
  useGetDashboardCountsQuery,
  useLazyGetExportOrdersQuery,
  useBatchTransitionOrdersMutation,
  useBatchAssignOrdersMutation,
  useBulkUpdateOrdersMutation,
  useGetAvailableCouriersQuery,
  useGetOrderManifestItemsQuery,
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useResetEmployeePasswordMutation,
  useGetClientsQuery,
  useGetClientByIdQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useGetClientOrdersQuery,
  useGetClientOrderSuggestionsQuery,
  useCreatePhoneNumberMutation,
  useUpdatePhoneNumberMutation,
  useDeletePhoneNumberMutation,
  useCreateClientAddressMutation,
  useUpdateClientAddressMutation,
  useDeleteClientAddressMutation,
  useCreateAuthorizedRecipientMutation,
  useUpdateAuthorizedRecipientMutation,
  useDeleteAuthorizedRecipientMutation,
  useImportClientsMutation,
  usePhoneBulkUpdateMutation,
  useLazyGetClientDuplicatesQuery,
  useMergeClientsMutation,
  useLinkClientTagMutation,
  useUnlinkClientTagMutation,
  useGetTagsQuery,
  useGetAllTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetConfigurationsQuery,
  useCreateConfigurationMutation,
  useUpdateConfigurationMutation,
  useDeleteConfigurationMutation,
  useGetBagTypesQuery,
  useCreateBagTypeMutation,
  useUpdateBagTypeMutation,
  useDeleteBagTypeMutation,
  useGetProviderDetailQuery,
  useUpdateProviderTimezoneMutation,
  useGetProviderLogoQuery,
  useUploadProviderLogoMutation,
  useGetAddressesQuery,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetIotCredsQuery,
  useGetReportParametersQuery,
  useFetchReportMutation,
  useFetchImmobilityReportMutation,
  useGetLinksQuery,
  useCreateLinkMutation,
  useAcceptLinkMutation,
  useDeleteLinkMutation,
  useUpdateLinkConfigMutation,
  useReactivateLinkMutation,
  useGetCouriersQuery,
  useGetCourierByIdQuery,
  useCreateCourierMutation,
  useUpdateCourierMutation,
  useToggleCourierMutation,
  useGetCourierDevicesQuery,
  useSendCourierPushMessageMutation,
  useSendCourierRemoteCommandMutation,
  useGetOrderTraceabilitiesQuery,
  useGetRecurringOrdersQuery,
  useGetRecurringOrderByIdQuery,
  useUpdateRecurringOrderMutation,
  useSkipRecurringOrderDateMutation,
  useStopRecurringOrderMutation,
  useResumeRecurringOrderMutation,
} = appApi
